/**
 * Notification Normalizer Utility
 * 
 * Purpose: Normalize notifications from different sources (WebSocket and HTTP API)
 * into a unified data structure to enable deduplication and consistent handling.
 * 
 * Critical Issue Solved:
 * - WebSocket uses 'content' field, HTTP API uses 'message' field
 * - WebSocket uses 'update_orders' (plural), HTTP API uses 'update_order' (singular)
 * - Different timestamp fields and optional fields
 * 
 * @author BitPanel Team
 * @date 2025-10-28
 */

import md5 from 'md5';

/**
 * Normalized notification structure
 * 
 * @typedef {Object} NormalizedNotification
 * @property {string} id - Unique identifier (UUID for API, generated for WebSocket)
 * @property {string} title - Notification title (translated)
 * @property {string} message - Notification message/content (translated)
 * @property {boolean} update_wallet - Should invalidate wallet cache
 * @property {boolean} update_orders - Should invalidate orders cache (always plural)
 * @property {boolean} silent - Don't show toast notification if true
 * @property {string} created_at - ISO 8601 timestamp
 * @property {string} source - Source of notification: 'websocket' or 'api'
 * @property {number|null} type - Message type (0=NOTIFICATION, 1=WALLET, 2=ORDER) for WebSocket, null for API
 * @property {string|null} section - Optional section identifier
 * @property {string} hash - MD5 hash for deduplication
 */

/**
 * Normalize WebSocket notification to common format
 * 
 * WebSocket notifications come from NotifEngine with structure:
 * {
 *   type: 0,
 *   title: "Order Completed",
 *   content: "Your order #123...",
 *   update_wallet: true,
 *   update_orders: true,
 *   silent: false,
 *   timestamp: "2025-10-28T10:30:00.000000Z"
 * }
 * 
 * @param {Object} data - Raw WebSocket notification data
 * @returns {NormalizedNotification} Normalized notification
 */
export function normalizeWebSocketNotification(data) {
  // Generate unique ID for WebSocket messages (they don't have database IDs)
  const id = `ws_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Extract and normalize fields
  const normalized = {
    id,
    title: data.title || '',
    message: data.content || data.message || '',  // Map 'content' → 'message'
    update_wallet: data.update_wallet === true,
    update_orders: data.update_orders === true,   // Keep plural (already correct)
    silent: data.silent === true,
    created_at: data.timestamp || new Date().toISOString(),
    source: 'websocket',
    type: typeof data.type === 'number' ? data.type : null,
    section: data.section || null
  };
  
  // Generate hash for deduplication
  normalized.hash = generateNotificationHash(normalized);
  
  return normalized;
}

/**
 * Normalize HTTP API notification to common format
 * 
 * HTTP API notifications come from bitapi1 database with structure:
 * {
 *   id: "uuid",
 *   title: "Order Completed",
 *   message: "Your order #123...",
 *   update_order: true,
 *   update_wallet: true,
 *   created_at: "2025-10-28T10:30:00.000000Z"
 * }
 * 
 * @param {Object} data - Raw HTTP API notification data
 * @returns {NormalizedNotification} Normalized notification
 */
export function normalizeAPINotification(data) {
  const normalized = {
    id: data.id,
    title: data.title || '',
    message: data.message || '',  // Already uses 'message'
    update_wallet: data.update_wallet === true,
    update_orders: data.update_order === true,    // Map singular → plural
    silent: false,  // API notifications are never silent (always show in list)
    created_at: data.created_at,
    source: 'api',
    type: null,  // API notifications don't have type field
    section: null
  };
  
  // Generate hash for deduplication
  normalized.hash = generateNotificationHash(normalized);
  
  return normalized;
}

/**
 * Generate MD5 hash for notification deduplication
 * 
 * Hash is based on:
 * - Title
 * - Message
 * - Timestamp (rounded to minute for 60-second deduplication window)
 * 
 * This allows detecting duplicate notifications from different sources
 * (e.g., same notification sent via WebSocket and stored in database)
 * 
 * @param {NormalizedNotification} notification - Normalized notification
 * @returns {string} MD5 hash
 */
export function generateNotificationHash(notification) {
  const hashContent = {
    title: notification.title,
    message: notification.message,
    // Round timestamp to minute for 60-second deduplication window
    // This allows notifications within the same minute to be considered duplicates
    timestamp: Math.floor(new Date(notification.created_at).getTime() / 60000)
  };
  
  return md5(JSON.stringify(hashContent));
}

/**
 * Check if notification is duplicate
 * 
 * Compares hash of new notification against existing notifications
 * to detect duplicates from different sources.
 * 
 * @param {NormalizedNotification} notification - Notification to check
 * @param {NormalizedNotification[]} existingNotifications - Array of existing notifications
 * @returns {boolean} True if duplicate found
 */
export function isDuplicateNotification(notification, existingNotifications) {
  if (!existingNotifications || existingNotifications.length === 0) {
    return false;
  }
  
  const hash = notification.hash || generateNotificationHash(notification);
  
  return existingNotifications.some(existing => {
    const existingHash = existing.hash || generateNotificationHash(existing);
    return existingHash === hash;
  });
}

/**
 * Normalize notification from any source
 * 
 * Auto-detects source and applies appropriate normalization.
 * 
 * Detection logic:
 * - If has 'content' field → WebSocket
 * - If has 'update_order' (singular) → HTTP API
 * - Otherwise, check for other indicators
 * 
 * @param {Object} data - Raw notification data
 * @param {string} [explicitSource] - Explicitly specify source ('websocket' or 'api')
 * @returns {NormalizedNotification} Normalized notification
 */
export function normalizeNotification(data, explicitSource = null) {
  // If source is explicitly specified, use it
  if (explicitSource === 'websocket') {
    return normalizeWebSocketNotification(data);
  }
  if (explicitSource === 'api') {
    return normalizeAPINotification(data);
  }
  
  // Auto-detect source based on data structure
  
  // WebSocket indicators:
  // - Has 'content' field (WebSocket uses 'content', API uses 'message')
  // - Has 'timestamp' field (WebSocket uses 'timestamp', API uses 'created_at')
  // - Has 'type' field (WebSocket has type 0/1/2, API doesn't)
  if (data.content !== undefined || data.timestamp !== undefined || typeof data.type === 'number') {
    return normalizeWebSocketNotification(data);
  }
  
  // HTTP API indicators:
  // - Has 'update_order' (singular) field (API uses singular, WebSocket uses plural)
  // - Has UUID-format 'id' field
  if (data.update_order !== undefined || (data.id && typeof data.id === 'string' && data.id.includes('-'))) {
    return normalizeAPINotification(data);
  }
  
  // Default to WebSocket if can't determine
  console.warn('[NotificationNormalizer] Could not auto-detect source, defaulting to WebSocket', data);
  return normalizeWebSocketNotification(data);
}

/**
 * Batch normalize multiple notifications
 * 
 * @param {Object[]} notifications - Array of raw notifications
 * @param {string} [source] - Source of all notifications ('websocket' or 'api')
 * @returns {NormalizedNotification[]} Array of normalized notifications
 */
export function normalizeNotifications(notifications, source = null) {
  if (!Array.isArray(notifications)) {
    console.error('[NotificationNormalizer] Expected array, got:', typeof notifications);
    return [];
  }
  
  return notifications.map(notification => normalizeNotification(notification, source));
}

/**
 * Merge and deduplicate notifications from multiple sources
 * 
 * Combines notifications from WebSocket and HTTP API, removing duplicates.
 * Keeps the most recent version of each notification.
 * 
 * @param {NormalizedNotification[]} websocketNotifications - Notifications from WebSocket
 * @param {NormalizedNotification[]} apiNotifications - Notifications from HTTP API
 * @returns {NormalizedNotification[]} Merged and deduplicated notifications
 */
export function mergeNotifications(websocketNotifications, apiNotifications) {
  const merged = [];
  const seenHashes = new Set();
  
  // Combine both arrays
  const allNotifications = [...websocketNotifications, ...apiNotifications];
  
  // Sort by created_at (newest first)
  allNotifications.sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return dateB - dateA;
  });
  
  // Deduplicate
  for (const notification of allNotifications) {
    const hash = notification.hash || generateNotificationHash(notification);
    
    if (!seenHashes.has(hash)) {
      seenHashes.add(hash);
      merged.push(notification);
    }
  }
  
  return merged;
}

/**
 * Filter notifications by criteria
 * 
 * @param {NormalizedNotification[]} notifications - Notifications to filter
 * @param {Object} criteria - Filter criteria
 * @param {string} [criteria.source] - Filter by source ('websocket' or 'api')
 * @param {number} [criteria.type] - Filter by type (0/1/2)
 * @param {boolean} [criteria.silent] - Filter by silent flag
 * @param {boolean} [criteria.update_wallet] - Filter by update_wallet flag
 * @param {boolean} [criteria.update_orders] - Filter by update_orders flag
 * @returns {NormalizedNotification[]} Filtered notifications
 */
export function filterNotifications(notifications, criteria) {
  return notifications.filter(notification => {
    if (criteria.source && notification.source !== criteria.source) {
      return false;
    }
    if (criteria.type !== undefined && notification.type !== criteria.type) {
      return false;
    }
    if (criteria.silent !== undefined && notification.silent !== criteria.silent) {
      return false;
    }
    if (criteria.update_wallet !== undefined && notification.update_wallet !== criteria.update_wallet) {
      return false;
    }
    if (criteria.update_orders !== undefined && notification.update_orders !== criteria.update_orders) {
      return false;
    }
    return true;
  });
}

export default {
  normalizeWebSocketNotification,
  normalizeAPINotification,
  normalizeNotification,
  normalizeNotifications,
  generateNotificationHash,
  isDuplicateNotification,
  mergeNotifications,
  filterNotifications
};

